import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { motion, useScroll, useTransform } from 'framer-motion';
import { BookOpen, Heart, Trophy, Users, ArrowRight, Sparkles, Globe, Shield, Zap, Apple, Smartphone } from 'lucide-react';

export default function Landing() {
  const { scrollYProgress } = useScroll();

  const y1 = useTransform(scrollYProgress, [0, 1], [0, -300]);
  const y2 = useTransform(scrollYProgress, [0, 1], [0, -150]);
  const y3 = useTransform(scrollYProgress, [0, 1], [0, -450]);
  const opacity1 = useTransform(scrollYProgress, [0, 0.3], [1, 0]);
  const scale1 = useTransform(scrollYProgress, [0, 0.3], [1, 0.9]);

  return (
    <div className="relative min-h-screen overflow-x-hidden bg-white dark:bg-gray-900">
      <Helmet>
        <title>Verse4All – Discover Biblical Wisdom & Test Your Knowledge</title>
        <meta name="description" content="Explore Bible verses, test your biblical knowledge with trivia, and listen to inspiring Christian music. Your daily companion for spiritual growth." />
      </Helmet>

      {/* Hero Section with Asymmetric Design */}
      <section className="relative min-h-screen flex items-center overflow-hidden">
        {/* Parallax Background Image */}
        <motion.div style={{ y: y3 }} className="absolute inset-0">
          <div
            className="absolute inset-0 bg-cover bg-center opacity-15 dark:opacity-8"
            style={{
              backgroundImage: `url('https://images.unsplash.com/photo-1519491050282-cf00c82424b4?w=1600&q=80')`,
              filter: 'blur(2px)'
            }}
          />
          <div className="absolute inset-0 bg-gradient-to-br from-purple-50 via-white to-fuchsia-50/50 dark:from-gray-900 dark:via-purple-950/50 dark:to-gray-900" />
        </motion.div>

        {/* Animated Gradient Orbs */}
        <motion.div
          style={{ y: y1 }}
          className="absolute -top-40 -right-40 w-[600px] h-[600px] bg-gradient-to-br from-purple-400/30 to-fuchsia-500/30 rounded-full blur-3xl"
        />
        <motion.div
          style={{ y: y2 }}
          className="absolute -bottom-40 -left-40 w-[500px] h-[500px] bg-gradient-to-tr from-violet-400/20 to-purple-500/20 rounded-full blur-3xl"
        />

        {/* Hero Content - Asymmetric Layout */}
        <div className="relative z-10 container mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            {/* Left Column - Text Content */}
            <motion.div
              style={{ opacity: opacity1, scale: scale1 }}
              className="text-left"
            >
              <motion.div
                initial={{ opacity: 0, x: -50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
              >
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-purple-100 dark:bg-purple-900/30 border border-purple-200 dark:border-purple-700 mb-8">
                  <Sparkles className="w-4 h-4 text-purple-600 dark:text-purple-400" />
                  <span className="text-sm font-semibold text-purple-700 dark:text-purple-300">
                    Transform Your Spiritual Journey
                  </span>
                </div>

                <h1 className="text-5xl sm:text-6xl lg:text-7xl xl:text-8xl font-black text-gray-900 dark:text-white mb-8 leading-[1.05]">
                  Experience
                  <span className="block premium-text mt-2">God's Word</span>
                  <span className="block text-gray-900 dark:text-white mt-2">Like Never Before</span>
                </h1>

                <p className="text-xl sm:text-2xl text-gray-700 dark:text-gray-300 mb-10 leading-relaxed max-w-xl">
                  Connect with scripture through innovative study tools, engage in community prayer, and grow your faith daily.
                </p>

                <div className="flex flex-col gap-6">
                  <div className="flex flex-col sm:flex-row gap-4">
                    <Link
                      to="/"
                      className="group px-10 py-5 premium-button text-white rounded-2xl font-bold text-lg flex items-center justify-center gap-3 hover:gap-4 transition-all"
                    >
                      Start Free Today
                      <ArrowRight className="w-5 h-5" />
                    </Link>
                    <a
                      href="#features"
                      className="px-10 py-5 bg-white dark:bg-gray-800 text-purple-600 dark:text-purple-400 rounded-2xl font-bold text-lg border-2 border-purple-200 dark:border-purple-700 hover:border-purple-400 dark:hover:border-purple-500 transition-all text-center"
                    >
                      Explore Features
                    </a>
                  </div>

                  {/* App Store Buttons */}
                  <div className="flex flex-col sm:flex-row gap-4">
                    <a
                      href="#"
                      className="group flex items-center gap-3 px-6 py-3 bg-black dark:bg-white rounded-xl hover:bg-gray-800 dark:hover:bg-gray-100 transition-all"
                    >
                      <Apple className="w-8 h-8 text-white dark:text-black" />
                      <div className="text-left">
                        <div className="text-xs text-gray-300 dark:text-gray-600">Download on the</div>
                        <div className="text-lg font-bold text-white dark:text-black">App Store</div>
                      </div>
                    </a>
                    <a
                      href="#"
                      className="group flex items-center gap-3 px-6 py-3 bg-black dark:bg-white rounded-xl hover:bg-gray-800 dark:hover:bg-gray-100 transition-all"
                    >
                      <Smartphone className="w-8 h-8 text-white dark:text-black" />
                      <div className="text-left">
                        <div className="text-xs text-gray-300 dark:text-gray-600">GET IT ON</div>
                        <div className="text-lg font-bold text-white dark:text-black">Google Play</div>
                      </div>
                    </a>
                  </div>
                </div>
              </motion.div>
            </motion.div>

            {/* Right Column - Visual Element */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
              className="relative"
            >
              <div className="relative">
                {/* Main Video Card */}
                <motion.div
                  animate={{
                    y: [0, -20, 0],
                    rotate: [0, 2, 0]
                  }}
                  transition={{
                    duration: 8,
                    repeat: Infinity,
                    ease: "easeInOut"
                  }}
                  className="relative rounded-3xl overflow-hidden shadow-2xl shadow-purple-500/30 border-4 border-white dark:border-gray-800"
                >
                  <video
                    src="/video/verse4all_intro.mp4"
                    autoPlay
                    loop
                    muted
                    playsInline
                    className="w-full h-[600px] object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-purple-900/50 via-transparent to-transparent" />
                </motion.div>

                {/* Floating Stats Cards */}
                <motion.div
                  animate={{ y: [0, 15, 0] }}
                  transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
                  className="absolute -bottom-8 -left-8 bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-xl border border-purple-200 dark:border-purple-700"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-xl bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
                      <Users className="w-6 h-6 text-purple-600 dark:text-purple-400" />
                    </div>
                    <div>
                      <div className="text-2xl font-black text-gray-900 dark:text-white">10K+</div>
                      <div className="text-sm text-gray-600 dark:text-gray-400">Active Users</div>
                    </div>
                  </div>
                </motion.div>

                <motion.div
                  animate={{ y: [0, -15, 0] }}
                  transition={{ duration: 7, repeat: Infinity, ease: "easeInOut", delay: 0.5 }}
                  className="absolute -top-8 -right-8 bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-xl border border-purple-200 dark:border-purple-700"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-xl bg-fuchsia-100 dark:bg-fuchsia-900/30 flex items-center justify-center">
                      <BookOpen className="w-6 h-6 text-fuchsia-600 dark:text-fuchsia-400" />
                    </div>
                    <div>
                      <div className="text-2xl font-black text-gray-900 dark:text-white">4</div>
                      <div className="text-sm text-gray-600 dark:text-gray-400">Languages</div>
                    </div>
                  </div>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Features Section - Organic Cards Layout */}
      <section id="features" className="relative py-32 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-white via-purple-50/50 to-white dark:from-gray-900 dark:via-purple-950/30 dark:to-gray-900">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.8 }}
            className="text-center mb-20"
          >
            <h2 className="text-4xl sm:text-5xl lg:text-6xl font-black text-gray-900 dark:text-white mb-6">
              Everything You Need for
              <span className="block premium-text mt-2">Spiritual Growth</span>
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-400 max-w-3xl mx-auto">
              A comprehensive platform designed to enrich your faith journey with powerful tools and community support
            </p>
          </motion.div>

          {/* Staggered Grid Layout */}
          <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-8">
            {[
              {
                icon: BookOpen,
                title: 'Multi-Language Bible',
                description: 'Access scriptures in English, French, Spanish, and Haitian Creole with powerful search capabilities.',
                image: 'https://images.unsplash.com/photo-1519491050282-cf00c82424b4?w=600&q=80',
                delay: 0
              },
              {
                icon: Trophy,
                title: 'Interactive Trivia',
                description: 'Test your biblical knowledge with engaging quizzes, compete in contests, and earn achievements.',
                image: 'https://images.unsplash.com/photo-1528605105345-5344ea20e269?w=600&q=80',
                delay: 0.1
              },
              {
                icon: Heart,
                title: 'Prayer Community',
                description: 'Share prayer requests, pray for others, and experience the power of collective faith.',
                image: 'https://images.unsplash.com/photo-1438232992991-995b7058bbb3?w=600&q=80',
                delay: 0.2
              },
              {
                icon: Globe,
                title: 'Global Reach',
                description: 'Connect with believers worldwide and share your spiritual journey across cultures.',
                image: 'https://images.unsplash.com/photo-1502691876148-a84978e59af8?w=600&q=80',
                delay: 0.3
              },
              {
                icon: Zap,
                title: 'AI-Powered Insights',
                description: 'Get personalized scripture recommendations and prayer support through advanced AI.',
                image: 'https://images.unsplash.com/photo-1515634928627-2a4e0dae3ddf?w=600&q=80',
                delay: 0.4
              },
              {
                icon: Shield,
                title: 'Safe & Secure',
                description: 'Your data and prayers are protected with enterprise-grade security and privacy.',
                image: 'https://images.unsplash.com/photo-1500964757637-c85e8a162699?w=600&q=80',
                delay: 0.5
              }
            ].map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 60 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{
                  duration: 0.7,
                  delay: feature.delay,
                  ease: [0.16, 1, 0.3, 1]
                }}
                className={index === 0 ? 'lg:col-span-2 lg:row-span-2' : ''}
              >
                <div className="group relative h-full rounded-2xl sm:rounded-3xl overflow-hidden bg-white dark:bg-gray-900 border border-purple-200/50 dark:border-purple-700/50 hover:border-purple-400 dark:hover:border-purple-500 shadow-lg hover:shadow-2xl hover:shadow-purple-500/20 transition-all duration-500">
                  {/* Image */}
                  <div className={`relative overflow-hidden ${index === 0 ? 'sm:h-80' : 'h-32 sm:h-48'}`}>
                    <div
                      className="absolute inset-0 bg-cover bg-center group-hover:scale-110 transition-transform duration-700"
                      style={{ backgroundImage: `url('${feature.image}')` }}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent" />
                    <div className="absolute bottom-2 left-2 sm:bottom-6 sm:left-6 w-10 h-10 sm:w-14 sm:h-14 rounded-xl sm:rounded-2xl bg-purple-600/90 backdrop-blur-sm flex items-center justify-center">
                      <feature.icon className="w-5 h-5 sm:w-7 sm:h-7 text-white" />
                    </div>
                  </div>

                  {/* Content */}
                  <div className="p-3 sm:p-8">
                    <h3 className="text-sm sm:text-2xl font-bold sm:font-black text-gray-900 dark:text-white mb-1 sm:mb-3 group-hover:text-purple-600 dark:group-hover:text-purple-400 transition-colors line-clamp-1">
                      {feature.title}
                    </h3>
                    <p className="text-gray-600 dark:text-gray-400 text-xs sm:text-base leading-relaxed line-clamp-2">
                      {feature.description}
                    </p>
                  </div>

                  {/* Hover Effect */}
                  <div className="absolute inset-0 bg-gradient-to-br from-purple-500/0 to-fuchsia-500/0 group-hover:from-purple-500/5 group-hover:to-fuchsia-500/5 transition-all duration-500 pointer-events-none" />
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section with Image Background */}
      <section className="relative py-40 px-4 sm:px-6 lg:px-8 overflow-hidden">
        {/* Parallax Background */}
        <motion.div style={{ y: y2 }} className="absolute inset-0">
          <div
            className="absolute inset-0 bg-cover bg-center opacity-20 dark:opacity-10"
            style={{ backgroundImage: `url('https://images.unsplash.com/photo-1484480974693-6ca0a78fb36b?w=1600&q=80')` }}
          />
          <div className="absolute inset-0 bg-gradient-to-br from-purple-600/40 via-violet-600/40 to-fuchsia-600/40 dark:from-purple-900/60 dark:via-violet-900/60 dark:to-fuchsia-900/60" />
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="relative max-w-5xl mx-auto text-center"
        >
          <div className="inline-block mb-8">
            <motion.div
              animate={{
                rotate: [0, 10, -10, 0],
                scale: [1, 1.1, 1]
              }}
              transition={{
                duration: 5,
                repeat: Infinity,
                ease: "easeInOut"
              }}
            >
              <span className="text-7xl">🙏</span>
            </motion.div>
          </div>

          <h2 className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-black text-white mb-8 leading-tight">
            Begin Your Journey
            <span className="block mt-2">of Faith Today</span>
          </h2>
          <p className="text-xl sm:text-2xl text-white/90 mb-12 max-w-3xl mx-auto leading-relaxed">
            Join thousands of believers discovering the joy of daily scripture, meaningful prayer, and authentic community.
          </p>
          <div className="flex flex-col items-center gap-6">
            <Link
              to="/"
              className="inline-flex items-center gap-3 px-12 py-6 bg-white text-purple-600 rounded-2xl font-black text-xl hover:scale-105 hover:shadow-2xl transition-all"
            >
              Get Started Free
              <ArrowRight className="w-6 h-6" />
            </Link>

            {/* App Store Buttons */}
            <div className="flex flex-col sm:flex-row gap-4">
              <a
                href="#"
                className="group flex items-center gap-3 px-6 py-3 bg-black rounded-xl hover:bg-gray-800 transition-all shadow-lg"
              >
                <Apple className="w-8 h-8 text-white" />
                <div className="text-left">
                  <div className="text-xs text-gray-300">Download on the</div>
                  <div className="text-lg font-bold text-white">App Store</div>
                </div>
              </a>
              <a
                href="#"
                className="group flex items-center gap-3 px-6 py-3 bg-black rounded-xl hover:bg-gray-800 transition-all shadow-lg"
              >
                <Smartphone className="w-8 h-8 text-white" />
                <div className="text-left">
                  <div className="text-xs text-gray-300">GET IT ON</div>
                  <div className="text-lg font-bold text-white">Google Play</div>
                </div>
              </a>
            </div>
          </div>
        </motion.div>
      </section>

      {/* Footer */}
      <footer className="relative bg-gray-900 dark:bg-black py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center space-y-4">
            <p className="text-gray-400 dark:text-gray-500 text-sm">
              © 2025 VERSE 4 All. All rights reserved.
            </p>
            <p className="text-lg text-gray-300 dark:text-gray-400 italic">
              "For the word of God is alive and active." - Hebrews 4:12
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
